import { format } from "date-fns";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "urql";

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { graphql } from "@/gen";
import { LayoutsQuery } from "@/gen/graphql";
import { useLimitOffsetPagination } from "@/utils/pagination";

const layoutsQuery = graphql(`
    query Layouts($limit: Int!, $offset: Int!) {
        layouts(input: { pagination: { limit: $limit, offset: $offset } }) {
            total
            items {
                id
                name
                publishAt
                unpublishAt
                createdAt
                updatedAt
            }
        }
    }
`);

export default function LayoutsRoute() {
    const [{ limit, offset }] = useLimitOffsetPagination();

    const [result] = useQuery({
        query: layoutsQuery,
        variables: {
            limit,
            offset,
        },
    });

    const [createLayoutRes, createLayout] = useMutation(createLayoutMutation);

    const { data, fetching } = result;

    return (
        <div className="m-4 flex flex-col gap-4">
            <PageHeader
                title="Layouts"
                actions={
                    <Button onClick={() => createLayout({ input: { cloneOf: null } })}>
                        Create empty layout
                    </Button>
                }
            />

            {createLayoutRes.error && <div>Error: {createLayoutRes.error.message}</div>}

            {data && data.layouts.items.length > 0 ? (
                <LayoutTable layouts={data.layouts.items} isFetching={fetching} />
            ) : fetching ? (
                <div>Loading</div>
            ) : (
                <div>No layouts</div>
            )}
        </div>
    );
}

const createLayoutMutation = graphql(`
    mutation createLayout($input: CreateLayoutInput!) {
        createLayout(input: $input) {
            id
        }
    }
`);

const deleteLayoutMutation = graphql(`
    mutation deleteLayout($input: DeleteLayoutInput!) {
        deleteLayout(input: $input) {
            id
        }
    }
`);

function LayoutTable({
    layouts,
}: {
    layouts: LayoutsQuery["layouts"]["items"];
    isFetching: boolean;
}) {
    const navigate = useNavigate();

    const [createLayoutRes, createLayout] = useMutation(createLayoutMutation);
    const [deleteLayoutRes, deleteLayout] = useMutation(deleteLayoutMutation);

    const activeLayoutId = useMemo(() => {
        const now = new Date();

        const publishedLayouts = layouts.filter(layout => {
            if (!layout.publishAt) {
                return false;
            }

            const publishDate = new Date(layout.publishAt);
            const unpublishDate = layout.unpublishAt ? new Date(layout.unpublishAt) : null;

            return publishDate <= now && (!unpublishDate || unpublishDate > now);
        });

        publishedLayouts.sort((a, b) => {
            const dateA = new Date(a.publishAt!);
            const dateB = new Date(b.publishAt!);
            return dateB.getTime() - dateA.getTime();
        });

        return publishedLayouts.length > 0 ? publishedLayouts[0].id : null;
    }, [layouts]);

    return (
        <>
            {createLayoutRes.error && <div>Error: {createLayoutRes.error.message}</div>}
            {deleteLayoutRes.error && <div>Error: {deleteLayoutRes.error.message}</div>}
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead>Publish at</TableHead>
                        <TableHead>&nbsp;</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {[...layouts]
                        .sort((a, b) => {
                            return (
                                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                            );
                        })
                        .map(layout => {
                            const isActive = layout.id === activeLayoutId;
                            return (
                                <TableRow
                                    key={layout.id}
                                    className="cursor-pointer"
                                    onClick={() => navigate(layout.id)}
                                >
                                    <TableCell>
                                        {layout.name || layout.id.substring(0, 6)}
                                    </TableCell>
                                    <TableCell>
                                        <span
                                            className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium text-white ${isActive ? "bg-green-500" : "bg-gray-500"}`}
                                        >
                                            {isActive ? "Active" : "Inactive"}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        {format(layout.createdAt, "yyyy-MM-dd HH:mm")}
                                    </TableCell>
                                    <TableCell>
                                        {format(layout.updatedAt, "yyyy-MM-dd HH:mm")}
                                    </TableCell>
                                    <TableCell>
                                        {layout.publishAt
                                            ? format(layout.publishAt, "yyyy-MM-dd HH:mm")
                                            : "-"}
                                    </TableCell>
                                    <TableCell className="flex gap-2">
                                        <Button
                                            onClick={ev => {
                                                ev.stopPropagation();
                                                createLayout({ input: { cloneOf: layout.id } });
                                            }}
                                        >
                                            Clone
                                        </Button>
                                        <Button
                                            onClick={ev => {
                                                ev.stopPropagation();
                                                window.confirm(
                                                    "You have unsaved changes. Are you sure you want to close this modal?"
                                                )
                                                    ? deleteLayout({
                                                          input: { layoutId: layout.id },
                                                      })
                                                    : null;
                                            }}
                                            variant="destructive"
                                        >
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                </TableBody>
            </Table>
        </>
    );
}
